import { useCallback, useEffect, useState } from "react";
import { FormImageUploader } from "@/components/shared/FormImageUploader";
import type { CommerceSettings, CommerceState } from "@stackje/site-builder";
import type { CommerceApi } from "../../api/commerceApi";
import { newShopId } from "../../lib/shop";
import { moneyInput, parseMoney } from "../../lib/shopMoney";
import { AreaError, Card, CheckRow, Combobox, Field, FieldGroup, SaveBar } from "../area";
import { likelyCountry, stripeCountryOptions } from "../../lib/shopLocale";

import { useShopLoad } from "./useShopLoad";
import { useShopAction } from "./useShopAction";
import { CommerceSkeleton, ShopActivity, ShopLoadError } from "./ShopLoading";

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
const countries = Array.from({ length: 26 * 26 }, (_, i) =>
  String.fromCharCode(65 + Math.floor(i / 26), 65 + (i % 26)),
)
  .filter((code) => {
    const name = countryNames.of(code);
    return name && name !== code;
  })
  .map((code) => ({ value: code, label: countryNames.of(code)!, keywords: code }))
  .sort((a, b) => a.label.localeCompare(b.label));
const stripeCountries = stripeCountryOptions();
function Country({
  value,
  onChange,
}: {
  value: string;
  onChange: (code: string) => void;
}) {
  return <Combobox value={value} options={countries} onChange={onChange} placeholder="Search countries" />;
}
type Draft = Omit<CommerceSettings, "regions"> & {
  regions: Array<
    Omit<CommerceSettings["regions"][number], "rates" | "freeOver"> & {
      rates: Array<{ id: string; name: string; amount: string }>;
      freeOver: string;
    }
  >;
};
const draftOf = (s: CommerceSettings, currency: string): Draft => ({
  ...s,
  regions: s.regions.map((r) => ({
    ...r,
    freeOver: r.freeOver === null ? "" : moneyInput(r.freeOver, currency),
    rates: r.rates.map((x) => ({
      ...x,
      amount: moneyInput(x.amount, currency),
    })),
  })),
});
const presets = [
  {
    label: "Bank transfer",
    instructions:
      "Transfer to [bank name and account number]. Use your order number as the reference.",
    pickupOnly: false,
  },
  {
    label: "DuitNow QR",
    instructions:
      "Scan the QR code and pay the order total. Use your order number as the reference.",
    pickupOnly: false,
  },
  {
    label: "Cash on pickup",
    instructions: "Pay when you collect your order.",
    pickupOnly: true,
  },
];
export function CommerceSettingsView({
  api,
  siteId,
  currency,
  timeZone,
  onLock,
  onDirty,
}: {
  api: CommerceApi;
  siteId: string;
  currency: string;
  /** The site's time zone, which suggests the Stripe account country. */
  timeZone?: string | null;
  onLock: (locked: boolean) => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const [state, setState] = useState<CommerceState | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [undo, setUndo] = useState<CommerceSettings | null>(null);
  const [imageUpload, setImageUpload] = useState<string | null>(null);
  const { busy, error, setError, run } = useShopAction();
  const read = useCallback(() => api.refresh(siteId), [api, siteId]);
  const loaded = useShopLoad(read, `${siteId}/${currency}`);
  const [country, setCountry] = useState(() => { const likely = likelyCountry(timeZone); return likely && stripeCountries.some((option) => option.value === likely) ? likely : ""; });
  const uploadImage = (key: string, file: File, apply: (image: NonNullable<CommerceSettings["emailLogo"]>) => void) => {
    let started = false;
    void run(async () => {
      started = true; setImageUpload(key);
      const image = await api.upload(siteId, file);
      if (image.mimeType === "image/svg+xml") throw new Error("Choose PNG, JPEG or WebP.");
      apply({ ...image, mimeType: image.mimeType });
    }).finally(() => { if (started) setImageUpload(null); });
  };
  const dirty = Boolean(
    state &&
      draft &&
      JSON.stringify(draft) !==
        JSON.stringify(draftOf(state.settings, currency)),
  );
  useEffect(() => {
    onDirty?.(dirty);
  }, [dirty, onDirty]);
  useEffect(() => {
    if (!loaded.data) return;
    const next = loaded.data;
    setState(next); setDraft(draftOf(next.settings, currency));
    onLock(next.currencyLocked);
    if (next.account) setCountry(next.account.country);
  }, [loaded.data, currency, onLock]);
  const save = (settings?: CommerceSettings) =>
    run(async () => {
      if (!state || !draft) return;
      const next: CommerceSettings = settings ?? {
        ...draft,
        regions: draft.regions.map((r) => {
          const freeOver = r.freeOver.trim()
            ? parseMoney(r.freeOver, currency, "en")
            : null;
          if (r.freeOver.trim() && freeOver === null)
            throw new Error("Check the free-shipping amount.");
          return {
            ...r,
            freeOver,
            rates: r.rates.map((rate) => {
              const amount = parseMoney(rate.amount, currency, "en");
              if (amount === null) throw new Error("Check shipping prices.");
              return { ...rate, amount };
            }),
          };
        }),
      };
      const result = await api.save(siteId, next, state.rowVersion);
      setUndo(state.settings);
      setState(result);
      setDraft(draftOf(result.settings, currency));
    });
  if (!state || !draft)
    return loaded.error ? <ShopLoadError message={loaded.error} retry={loaded.retry} /> : <div aria-busy="true"><CommerceSkeleton /></div>;
  const methods = draft.manualMethods;
  const regions = draft.regions;
  const method = (
    id: string,
    change: Partial<CommerceSettings["manualMethods"][number]>,
  ) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            manualMethods: current.manualMethods.map((m) =>
              m.id === id ? { ...m, ...change } : m,
            ),
          }
        : current,
    );
  const region = (id: string, change: Partial<Draft["regions"][number]>) =>
    setDraft({
      ...draft,
      regions: regions.map((r) => (r.id === id ? { ...r, ...change } : r)),
    });
  const status = state.account
    ? state.account.ready
      ? "Ready"
      : "Needs details"
    : "Not connected";
  return (
    <div className="bw-area-stack bw-shop-commerce" aria-busy={busy || loaded.busy}>
      <fieldset disabled={busy || !loaded.data} className="bw-shop-locked bw-area-stack">
      <Card title="Payments" hint={status}>
        <p>
          Accept online payments through Stripe. Payments go directly to your
          Stripe account.
        </p>
        <p className="bw-area-hint">
          Stripe charges a fee on each sale.{" "}
          <a
            href={(state.account?.country ?? country) ? `https://stripe.com/${(state.account?.country ?? country).toLowerCase()}/pricing` : "https://stripe.com/pricing"}
            target="_blank"
            rel="noreferrer"
          >
            {(state.account?.country ?? country) ? <>See Stripe’s prices for {countryNames.of(state.account?.country ?? country)} ↗</> : "See Stripe’s prices ↗"}
          </a>
        </p>
        {!state.account && (
          <Field label="Your Stripe account country" hint={country ? undefined : "Choose the country your business is in."}>
            <Combobox value={country} options={stripeCountries} onChange={setCountry} placeholder="Search countries" />
          </Field>
        )}
        <div className="bw-actions">
          <button
            type="button"
            className={`bw-btn ${state.account?.ready ? "bw-btn--quiet" : "bw-btn--primary"}`}
            disabled={busy || dirty || (!state.account && !country)}
            title={
              dirty
                ? "Save or discard your changes before connecting Stripe."
                : undefined
            }
            onClick={() =>
              void run(async () => {
                window.location.href = await api.connect(siteId, country);
              })
            }
          >
            {busy ? <ShopActivity announce={false} label="Working…" /> : state.account?.ready ? "Manage connection" : "Connect Stripe"}
          </button>
          {state.account && (
            <a
              className="bw-btn bw-btn--quiet"
              href="https://dashboard.stripe.com"
              target="_blank"
              rel="noreferrer"
            >
              Manage in Stripe ↗
            </a>
          )}
        </div>
        <h3>Manual payment methods</h3>
        <p className="bw-area-hint">
          Instructions appear after the buyer places their order. You confirm
          payment yourself.
        </p>
        {methods.map((m) => (
          <Card
            key={m.id}
            title={m.label}
            actions={
              <button
                type="button"
                className="bw-btn bw-btn--text"
                onClick={() =>
                  setDraft({
                    ...draft,
                    manualMethods: methods.filter((x) => x.id !== m.id),
                  })
                }
              >
                Remove
              </button>
            }
          >
            <Field label="Method name">
              <input
                maxLength={100}
                value={m.label}
                onChange={(e) => method(m.id, { label: e.target.value })}
              />
            </Field>
            <Field label="Instructions">
              <textarea
                rows={3}
                maxLength={10000}
                value={m.instructions}
                onChange={(e) => method(m.id, { instructions: e.target.value })}
              />
            </Field>
            <CheckRow
              label="Only for pickup orders"
              checked={m.pickupOnly}
              onChange={(e) => method(m.id, { pickupOnly: e.target.checked })}
            />
            <div className="bw-area-field bw-shop-payment-image">
              <span className="bw-area-field-head">Payment image (optional)</span>
              <small className="bw-area-hint">Add a QR code or payment instructions. PNG, JPEG or WebP, up to 5 MB.</small>
              {busy && imageUpload === `method:${m.id}` && <ShopActivity label="Uploading image…" />}
              <FormImageUploader
                currentImage={m.image ? {
                  fullUrl: `/_builder/sites/${encodeURIComponent(siteId)}/shop/commerce/image/${m.image.objectKey.split("/").at(-1)}`,
                  previewUrl: `/_builder/sites/${encodeURIComponent(siteId)}/shop/commerce/image/${m.image.objectKey.split("/").at(-1)}`,
                } : null}
                onImageChange={() => method(m.id, { image: null })}
                onFileUpload={file => uploadImage(`method:${m.id}`, file, image => method(m.id, { image }))}
                acceptedTypes={["image/png", "image/jpeg", "image/webp"]}
                maxSize={5 * 1024 * 1024}
                disabled={busy}
                imagePreviewClassName="bw-shop-payment-thumb"
                dropzoneClassName="bw-shop-payment-upload"
              />
            </div>
          </Card>
        ))}
        <div className="bw-actions">
          {presets.map((p) => (
            <button
              type="button"
              className="bw-btn bw-btn--quiet"
              key={p.label}
              onClick={() =>
                setDraft({
                  ...draft,
                  manualMethods: [
                    ...methods,
                    { ...p, id: newShopId("payment"), image: null },
                  ],
                })
              }
            >
              Add {p.label}
            </button>
          ))}
          <button
            type="button"
            className="bw-btn bw-btn--text"
            onClick={() =>
              setDraft({
                ...draft,
                manualMethods: [
                  ...methods,
                  {
                    id: newShopId("payment"),
                    label: "New method",
                    instructions: "",
                    image: null,
                    pickupOnly: false,
                  },
                ],
              })
            }
          >
            Add another method
          </button>
        </div>
      </Card>
      <Card
        title="Shipping"
        description="Named rates apply once per order."
        actions={
          <button
            type="button"
            className="bw-btn bw-btn--text"
            onClick={() =>
              setDraft({
                ...draft,
                regions: [
                  ...regions,
                  {
                    id: newShopId("region"),
                    name: "New region",
                    countries: [{ code: "MY", states: [] }],
                    rates: [
                      {
                        id: newShopId("rate"),
                        name: "Standard",
                        amount: moneyInput(0, currency),
                      },
                    ],
                    freeOver: "",
                  },
                ],
              })
            }
          >
            Add region
          </button>
        }
      >
        {!regions.length && (
          <p>
            No shipping regions yet. Buyers can still buy downloads or collect
            an order.
          </p>
        )}
        {regions.map((r) => (
          <Card
            key={r.id}
            title={r.name}
            actions={
              <button
                type="button"
                className="bw-btn bw-btn--text"
                onClick={() =>
                  setDraft({
                    ...draft,
                    regions: regions.filter((x) => x.id !== r.id),
                  })
                }
              >
                Remove region
              </button>
            }
          >
            <Field label="Region name">
              <input
                value={r.name}
                maxLength={100}
                onChange={(e) => region(r.id, { name: e.target.value })}
              />
            </Field>
            {r.countries.map((c, i) => (
              <div key={i} className="bw-shop-shipping-row">
                <FieldGroup stack>
                  <Field label="Country">
                    <Country
                      value={c.code}
                      onChange={(code) =>
                        region(r.id, {
                          countries: r.countries.map((x, at) =>
                            at === i ? { ...x, code } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Field
                    label="States / provinces"
                    hint="Leave blank for the whole country. Separate names with commas."
                  >
                    <input
                      value={c.states.join(",")}
                      onChange={(e) =>
                        region(r.id, {
                          countries: r.countries.map((x, at) =>
                            at === i
                              ? { ...x, states: e.target.value.split(",") }
                              : x,
                          ),
                        })
                      }
                      onBlur={() =>
                        region(r.id, {
                          countries: r.countries.map((x) => ({
                            ...x,
                            states: x.states
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })),
                        })
                      }
                    />
                  </Field>
                </FieldGroup>
                <button
                  type="button"
                  className="bw-btn bw-btn--text bw-area-link"
                  onClick={() =>
                    region(r.id, {
                      countries: r.countries.filter((_, at) => at !== i),
                    })
                  }
                >
                  Remove country
                </button>
              </div>
            ))}
            <button
              type="button"
              className="bw-btn bw-btn--text bw-area-link bw-area-link--start"
              onClick={() =>
                region(r.id, {
                  countries: [...r.countries, { code: "SG", states: [] }],
                })
              }
            >
              Add country
            </button>
            {r.rates.map((rate) => (
              <div key={rate.id} className="bw-shop-shipping-row bw-shop-rate-row">
                <FieldGroup>
                  <Field label="Rate name">
                    <input
                      value={rate.name}
                      onChange={(e) =>
                        region(r.id, {
                          rates: r.rates.map((x) =>
                            x.id === rate.id
                              ? { ...x, name: e.target.value }
                              : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Field label={`Price (${currency})`}>
                    <input
                      inputMode="decimal"
                      value={rate.amount}
                      onChange={(e) =>
                        region(r.id, {
                          rates: r.rates.map((x) =>
                            x.id === rate.id
                              ? { ...x, amount: e.target.value }
                              : x,
                          ),
                        })
                      }
                    />
                  </Field>
                </FieldGroup>
                <button
                  type="button"
                  className="bw-btn bw-btn--text bw-area-link"
                  onClick={() =>
                    region(r.id, {
                      rates: r.rates.filter((x) => x.id !== rate.id),
                    })
                  }
                >
                  Remove rate
                </button>
              </div>
            ))}
            <button
              type="button"
              className="bw-btn bw-btn--text bw-area-link bw-area-link--start"
              onClick={() =>
                region(r.id, {
                  rates: [
                    ...r.rates,
                    { id: newShopId("rate"), name: "Express", amount: "" },
                  ],
                })
              }
            >
              Add rate
            </button>
            <Field
              label={`Free shipping for orders over (${currency})`}
              hint="Optional. The item subtotal must meet this amount."
            >
              <input
                inputMode="decimal"
                value={r.freeOver}
                onChange={(e) => region(r.id, { freeOver: e.target.value })}
              />
            </Field>
          </Card>
        ))}
      </Card>
      <Card title="Order emails" description="Emails use your shop name. Replies go to your account email.">
        <Field label="Footer note" hint="Optional. Included in every order email."><textarea rows={3} maxLength={2000} value={draft.emailFooter ?? ""} onChange={e => setDraft({ ...draft, emailFooter: e.target.value })} /></Field>
        <div className="bw-area-field"><span className="bw-area-field-head">Shop logo (optional)</span>
          {busy && imageUpload === "logo" && <ShopActivity label="Uploading image…" />}
          <FormImageUploader
            currentImage={draft.emailLogo ? { fullUrl: `/_builder/sites/${encodeURIComponent(siteId)}/shop/commerce/image/${draft.emailLogo.objectKey.split("/").at(-1)}`, previewUrl: `/_builder/sites/${encodeURIComponent(siteId)}/shop/commerce/image/${draft.emailLogo.objectKey.split("/").at(-1)}` } : null}
            onImageChange={() => setDraft(current => current ? { ...current, emailLogo: null } : current)}
            onFileUpload={file => uploadImage("logo", file, image => setDraft(current => current ? { ...current, emailLogo: image } : current))}
            acceptedTypes={["image/png", "image/jpeg", "image/webp"]} maxSize={5 * 1024 * 1024} disabled={busy} imagePreviewClassName="bw-shop-payment-thumb" dropzoneClassName="bw-shop-payment-upload" />
        </div>
      </Card>
      <Card title="Pickup">
        <CheckRow
          label="Offer pickup"
          checked={Boolean(draft.pickup)}
          onChange={(e) =>
            setDraft({
              ...draft,
              pickup: e.target.checked
                ? { address: "", instructions: "" }
                : null,
            })
          }
        />
        {draft.pickup && (
          <>
            <Field label="Pickup address">
              <textarea
                rows={2}
                value={draft.pickup.address}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    pickup: { ...draft.pickup!, address: e.target.value },
                  })
                }
              />
            </Field>
            <Field label="Instructions">
              <textarea
                rows={3}
                value={draft.pickup.instructions}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    pickup: { ...draft.pickup!, instructions: e.target.value },
                  })
                }
              />
            </Field>
            <p className="bw-area-hint">Pickup is free.</p>
          </>
        )}
      </Card>
      </fieldset>
      {busy && <ShopActivity label="Working…" />}
      {loaded.busy && <ShopActivity label="Loading payment settings…" />}
      {loaded.error && <ShopLoadError message={loaded.error} retry={loaded.retry} />}
      {error && <AreaError message={error} onDismiss={() => setError(null)} />}
      {dirty ? (
        <SaveBar
          state="dirty"
          busy={busy}
          onSave={() => void save()}
          onDiscard={() => setDraft(draftOf(state.settings, currency))}
        />
      ) : undo ? (
        <SaveBar state="saved" busy={busy} onUndo={() => void save(undo)} />
      ) : null}
    </div>
  );
}
